import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { getOrCreateProjectChat } from '../../services/QAgent';
import { useI18n } from '../../i18n';

/**
 * Wrapper component that handles the /qagent/project/:projectId route.
 * It fetches or creates the chat for the project and navigates to it.
 */
export const QAgentProjectChat: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const pc = t.qagentChat.projectChat;
  // The backend's message when there is one, else which translated fallback to show
  const [error, setError] = useState<{ text?: string; fallback: 'loadFailed' | 'unexpectedError' } | null>(null);

  useEffect(() => {
    if (!projectId) {
      navigate('/');
      return;
    }

    const loadProjectChat = async () => {
      setError(null);

      try {
        const response = await getOrCreateProjectChat(projectId);
        if (response.success && response.data) {
          // Navigate to the chat URL - this keeps the URL consistent
          // and lets QAgentChat handle everything from there
          // Pass projectId in state so sidebar can highlight the correct project
          navigate(`/qagent/${response.data.id}`, {
            replace: true,
            state: { fromProjectId: projectId },
          });
        } else {
          setError({ text: response.errors?.[0]?.title, fallback: 'loadFailed' });
        }
      } catch (err: any) {
        setError({ text: err.message, fallback: 'unexpectedError' });
      }
    };

    loadProjectChat();
  }, [projectId, navigate]);

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-destructive">{error.text || pc[error.fallback]}</p>
          <button
            onClick={() => navigate('/')}
            className="text-sm text-primary hover:underline"
          >
            {pc.goHome}
          </button>
        </div>
      </div>
    );
  }

  // Show loading while fetching/navigating
  return (
    <div className="h-full flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">{pc.loading}</p>
      </div>
    </div>
  );
};

export default QAgentProjectChat;
